import { Button } from '@progress/kendo-react-buttons';
import { WindowActionsBar } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn as Column, GridCustomCellProps } from '@progress/kendo-react-grid';
import { Loader } from "@progress/kendo-react-indicators";
import dayjs from 'dayjs';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUpdateEffect } from 'usehooks-ts';
import DateCell from '../../components/cells/DateCell';
import CenterDivPanel from '../../components/CenterDivPanel';
import KendoWindow from '../../components/KendoWindow';
import { getSplitBadgeOrder, getSplitBadgeOrderStop, getSplitLabel, getSplitTitle } from '../../components/SplitIcon';
import { fetchApi } from '../../services/api';
import { isNullOrWhiteSpace } from '../../utils/utils';

type Props = {
  OrderID: number;
  onClose: () => void;
};

type OrderSplitSummary = {
  Title: string;
  BillingOrder: Order;
  SplitOrders: Order[];
}

type Order = {
  OrderID: number;
  OrderNumber: number;
  OrderStatusName: string;
  DispatchStatusName: string;
  BilledVehicleTypeName: string;
  TotalPieces: number;
  TotalWeight: number;
  HazMat: boolean;
  DockLevel: boolean;
  ShipDateTime: Date;
  DeliveryDateTime: Date;
  ShipCustomerName: string;
  DeliveryCustomerName: string;
  ShipLocation: string;
  DeliveryLocation: string;
  TotalMiles: number;
  ModNum: number;

  ForwardOrderID: number;
  BackwardOrderID: number;
  BillingOrderID: number;
  TripID: number;
  TripNumber: number;
  VehicleID: number | null;
  VehicleNumber: string;
  DriverID: number | null;
  DriverNumber: string;
  TrailerID: number | null;
  TrailerNumber: string;
  CarrierID: number | null;
  CarrierName: string;
  IsOriginalBillingOrderView: boolean;

  OrderStops: OrderStop[];
}

type OrderStop = {
  OrderStopID: number;
  Sequence: number;
  Event: number;
  EventNumber: string;
  CustomerName: string;
  Location: string;
  ScheduledDateTime: Date;
  Distance: number;
  StopStatus: number;
  StopStatusNumber: string;
  IsSplitLocation: boolean;
}

const SplitOrderSummary = (props: Props) => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<OrderSplitSummary>();

  const refreshOrderDetails = useCallback(() => {
    setLoading(true);

    fetchApi(`/api/Order/OrderSplitSummary`, { OrderID: props.OrderID }, 'POST')
      .then((response: OrderSplitSummary) => {
        setDetails(response);
        setLoading(false);

        //if (response.Errors.length === 0 && (addOrderID > 0 || addOrderNumber > 0))
        //    setSelectedActionItemKey(ActionItemKeyType.AddOrder);
      })
      .catch(e => {
        setLoading(false);
        // If not problem details
        if (!e?.status) alert('Unable to load split summary');
      });
  }, [props.OrderID]);

  useEffect(() => {
    refreshOrderDetails();
  }, [refreshOrderDetails]);

  // Hide on navigate
  useUpdateEffect(() => {
    props.onClose();
  }, [location]);

  return (
    <KendoWindow title={`Order Split Summary`} onClose={props.onClose} autoSizePercentage={85}>
      <div className="container-fluid">
        {loading && <CenterDivPanel>
          <Loader type="converging-spinner" />
        </CenterDivPanel>}

        {details &&
          <div>
            {details.SplitOrders.map((splitOrder, index) => (
              <div className="card my-2" key={index}>
                <div className="card-body">
                  <OrderView order={splitOrder} splitIndex={index} />
                </div>
              </div>
            ))}

            <div className="hr bg-secondary mt-3 mb-3" style={{ height: '2px' }}></div>

            <div className="card my-2">
              <div className="card-body">
                <OrderView order={details.BillingOrder} />
              </div>
            </div>

          </div>}
      </div >
      <WindowActionsBar layout={'end'}>
        <Button onClick={props.onClose}>Close</Button>
      </WindowActionsBar>
    </KendoWindow>
  );
};
export default SplitOrderSummary;

type OrderViewProps = {
  order: Order;
  splitIndex?: number;
};

const OrderView: React.FC<OrderViewProps> = (props: OrderViewProps) => {
  const [order, setOrder] = useState<Order>();

  useEffect(() => {
    setOrder(props.order);
  }, [props.order]);

  const detailComponent = (props: any) => {
    const dataItem = props.dataItem as Order;

    return (
      <section>
        <div className="card my-1">
          <div className="card-body">
            <h5 className="card-title text-left">Order Stops</h5>
            <table className="table table-hover table-striped mb-0">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Name</th>
                  <th>Location</th>
                  <th>Distance</th>
                  <th>Date/Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dataItem.OrderStops.map((orderStop) => {
                  return (<tr key={orderStop.OrderStopID}>
                    <td>{orderStop.EventNumber}</td>
                    <td>{orderStop.CustomerName}</td>
                    <td>{orderStop.Location}{<><br />{dataItem.IsOriginalBillingOrderView === false && getSplitBadgeOrderStop(orderStop.IsSplitLocation)}</>}</td>
                    <td>{orderStop.Distance}</td>
                    <td>{dayjs.utc(orderStop.ScheduledDateTime).tz("America/New_York").format("MM/DD/YYYY HH:mm")}</td>
                    <td>{orderStop.StopStatusNumber}</td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  };

  const LocationCell = (props: GridCustomCellProps & { locationType: string }) => {
    const dataItem = props.dataItem as Order;

    let splitShipperSuffix: JSX.Element;
    let splitDeliverySuffix: JSX.Element;

    if (dataItem.IsOriginalBillingOrderView === false) {
      if (dataItem.BackwardOrderID > 0)
        splitShipperSuffix = getSplitBadgeOrder(dataItem.BackwardOrderID, dataItem.ForwardOrderID);
      if (dataItem.ForwardOrderID > 0)
        splitDeliverySuffix = getSplitBadgeOrder(dataItem.BackwardOrderID, dataItem.ForwardOrderID);
    }

    return (
      <td {...props.tdProps}>
        {props.locationType === 'Shipper' ? dataItem.ShipCustomerName : dataItem.DeliveryCustomerName}
        <br />
        {props.locationType === 'Shipper' ?
          <>{dataItem.ShipLocation}{splitShipperSuffix && <><br />{splitShipperSuffix}</>}</> :
          <>{dataItem.DeliveryLocation}{splitDeliverySuffix && <><br />{splitDeliverySuffix}</>}</>
        }
      </td>
    );
  };

  const MileCell = (props: GridCustomCellProps) => {
    const dataItem = props.dataItem as Order;

    return (
      <td {...props.tdProps}>
        {dataItem.TotalMiles}<br />
        {dataItem.OrderStatusName}
      </td>
    );
  };

  const TripCell = (props: GridCustomCellProps) => {
    const dataItem = props.dataItem as Order;

    if (dataItem.TripNumber === 0)
      return (<td {...props.tdProps}>{dataItem.DispatchStatusName}</td>);

    if (isNullOrWhiteSpace(dataItem.CarrierName)) {
      return (
        <td {...props.tdProps}>
          <Link style={{ color: "#007bff" }} to={`/Trip/${dataItem.TripID}`}>{dataItem.TripNumber}</Link><br />
          <Link style={{ color: "#007bff" }} to={`/Assets/Vehicle/${dataItem.VehicleID}`}>{dataItem.VehicleNumber}</Link><br />
          <Link style={{ color: "#007bff" }} to={`/Assets/Driver/${dataItem.DriverID}`}>{dataItem.DriverNumber}</Link>
          {isNullOrWhiteSpace(dataItem.TrailerNumber) === false && <>
            <br /><Link style={{ color: "#007bff" }} to={`/Assets/Trailer/${dataItem.TrailerID}`}>{dataItem.TrailerNumber}</Link>
          </>}
          <br />{dataItem.DispatchStatusName}
        </td >
      );
    }

    return (
      <td {...props.tdProps}>
        <Link style={{ color: "#007bff" }} to={`/Trip/${dataItem.TripID}`}>{dataItem.TripNumber}</Link><br />
        <Link style={{ color: "#007bff" }} to={`/Assets/Carrier/${dataItem.CarrierID}`}>{dataItem.CarrierName}</Link><br />
        {dataItem.DispatchStatusName}
      </td>
    );
  };

  return (
    <div>
      <h5 className="card-title text-left">
        {(order?.IsOriginalBillingOrderView === false) ? `${props.splitIndex + 1} - ${getSplitTitle(order?.BackwardOrderID, order?.ForwardOrderID)}` : 'Original Billing Order'}
        &nbsp;
        {(order?.IsOriginalBillingOrderView === false) && getSplitLabel(order?.BackwardOrderID, order?.ForwardOrderID)}
      </h5>
      <Grid
        data={order ? new Array(order) : undefined}
        detail={detailComponent}
        dataItemKey='OrderID'
      >
        <Column field="OrderNumber" title="Order #" cells={{ data: (props) => <td {...props.tdProps}>
          <Link style={{ color: "#007bff" }} to={`/Order/${props.dataItem.OrderID}`}>{props.dataItem.OrderNumber}</Link>
        </td> }} />
        <Column field="ShipLocation" title="From"
          cells={{
            data: (cellProps) => <LocationCell {...cellProps} locationType='Shipper' />
          }} />
        <Column field="ShipDateTime" title="Pick-Up Date" cells={{ data: DateCell }} />
        <Column field="DeliveryLocation" title="To"
          cells={{
            data: (cellProps) => <LocationCell {...cellProps} locationType='Consignee' />
          }} />
        <Column field="DeliveryDateTime" title="Delivery Date" cells={{ data: DateCell }} />
        <Column field="TotalMiles" title="Miles/Status" cells={{ data: (cellProps) => <MileCell {...cellProps} /> }} />
        <Column field="TripNumber" title="Trip Info" cells={{ data: (cellProps) => <TripCell {...cellProps} /> }} />
      </Grid>
    </div>
  );
};
